import { createRef, useEffect } from "react";
import {
  Button,
  Col,
  DatePicker,
  Divider,
  Form,
  Input,
  Modal,
  Row,
  Select,
} from "antd";
import _ from "lodash";

import {
  FIELD_KEYS,
  FIELD_LABELS,
  FIELD_ORDERS,
  HISTORICAL_ONLY_FIELDS,
  TABLE_SORTABLE_FIELDS,
} from "constants/ErrorFields";
import { FILTER_KEYS, FILTER_LABELS } from "constants/table";
import FormItemSorters from "components/Common/FormItemSorters";
import FormSubmitButton from "components/Common/FormSubmitButton";
import useFetch from "hooks/useFetch";

// ###### Constants ######

const { useForm } = Form;
const { RangePicker } = DatePicker;

// ###### Component ######

const AdvancedSearchModal = ({
  isHistoricalView,
  showModal,
  filters,
  searchPlaceholder,
  onShowModalChange,
  onFiltersChange,
}) => {
  const {
    loading: loadingErrorCodeOptions,
    responseData: errorCodeOptionsResponseData,
    doFetch: fetchErrorCodeOptions,
  } = useFetch("/unitErrors/getErrorCodeOptions", "GET", []);
  const {
    loading: loadingResolveActionOptions,
    responseData: resolveActionOptionsResponseData,
    doFetch: fetchResolveActionOptions,
  } = useFetch("/unitErrors/getResolveActionOptions", "GET", []);
  const [form] = useForm();
  const formRef = createRef();
  const loading = loadingErrorCodeOptions || loadingResolveActionOptions;

  const errorCodeOptions = errorCodeOptionsResponseData.data ?? [];
  const resolveActionOptions = resolveActionOptionsResponseData.data ?? [];

  const sortByFields = isHistoricalView
    ? TABLE_SORTABLE_FIELDS
    : _.difference(TABLE_SORTABLE_FIELDS, HISTORICAL_ONLY_FIELDS);
  const sortByOptions = _.intersection(FIELD_ORDERS, sortByFields).map(
    (field) => ({
      label: FIELD_LABELS[field],
      value: field,
    })
  );

  const closeModal = (reset = true) => {
    onShowModalChange(false);
    if (reset) {
      form.resetFields();
    }
  };

  const handleAdvancedSearch = (values) => {
    onFiltersChange(values);
    closeModal(false);
  };

  useEffect(() => {
    fetchErrorCodeOptions();
    fetchResolveActionOptions();
  }, []);

  return (
    <>
      <Modal
        title="Advanced Search"
        open={showModal}
        loading={loading}
        onCancel={closeModal}
        footer={() => (
          <>
            <Button onClick={closeModal}>Cancel</Button>,
            <FormSubmitButton
              form={form}
              label="Search"
              buttonProps={{ onClick: () => formRef.current.submit() }}
            />
          </>
        )}
        wrapClassName="modal-xl-title"
      >
        <Form
          form={form}
          ref={formRef}
          initialValues={filters}
          onFinish={handleAdvancedSearch}
          layout="vertical"
        >
          <Form.Item
            label={FILTER_LABELS[FILTER_KEYS.SEARCH]}
            name={FILTER_KEYS.SEARCH}
          >
            <Input placeholder={searchPlaceholder} allowClear={true} />
          </Form.Item>
          <Divider orientation="left" orientationMargin="0">
            Filters
          </Divider>
          <Row gutter={[16, 0]}>
            <Col span="12">
              <Form.Item
                label={FIELD_LABELS[FIELD_KEYS.UNIT_START_DATE_TIME]}
                name={FIELD_KEYS.UNIT_START_DATE_TIME}
              >
                <RangePicker allowEmpty={[true, true]} />
              </Form.Item>
            </Col>
            <Col span="12">
              <Form.Item
                label={FIELD_LABELS[FIELD_KEYS.ERROR_DATE_TIME]}
                name={FIELD_KEYS.ERROR_DATE_TIME}
              >
                <RangePicker allowEmpty={[true, true]} />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={[16, 0]}>
            <Col span="12">
              <Form.Item
                label={FIELD_LABELS[FIELD_KEYS.ERROR_CODE]}
                name={FIELD_KEYS.ERROR_CODE}
                style={{ marginBottom: 8 }}
              >
                <Select
                  options={errorCodeOptions}
                  optionFilterProp="label"
                  mode="multiple"
                  allowClear={true}
                />
              </Form.Item>
            </Col>
            {isHistoricalView && (
              <Col span="12">
                <Form.Item
                  label={FIELD_LABELS[FIELD_KEYS.RESOLVE_ACTION]}
                  name={FIELD_KEYS.RESOLVE_ACTION}
                  style={{ marginBottom: 8 }}
                >
                  <Select
                    options={resolveActionOptions}
                    optionFilterProp="label"
                    mode="multiple"
                    allowClear={true}
                  />
                </Form.Item>
              </Col>
            )}
          </Row>
          <Divider orientation="left" orientationMargin="0">
            {FILTER_LABELS[FILTER_KEYS.SORTERS]}
          </Divider>
          <FormItemSorters form={form} sortByOptions={sortByOptions} />
        </Form>
      </Modal>
    </>
  );
};

export default AdvancedSearchModal;
